import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteProps, AutocompleteRenderOptionState, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";
import Api from "../../../../../core/services/api/api";

/**
 * Interface representing a PartSupportVendorStatus option in the autocomplete dropdown.
 * This structure matches the API response format for PartSupportVendorStatus data.
 *
 * @property id - Unique identifier for the PartSupportVendorStatus
 * @property name - Display name of the PartSupportVendorStatus
 * @property city - City where the PartSupportVendorStatus is located
 */
interface PartSupportVendorStatusOption {
    id: number;
    title?: string;
}

/**
 * Props interface for the PartSupportVendorStatusAutocomplete component.
 * Extends the base autocomplete functionality with PartSupportVendorStatus-specific features.
 *
 * @property textFieldProps - Additional props to pass to the TextField component
 * @property form - Whether the component is used within a form
 * @property name - Name attribute for form integration
 * @property required - Whether the field is required in forms
 * @property setLoadingOptions - Callback to handle loading state changes
 * @property value - Currently selected PartSupportVendorStatus option
 * @property onChange - Callback fired when selection changes
 * @property renderOption - Custom render function for dropdown options
 */
interface PartSupportVendorStatusAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<PartSupportVendorStatusOption, M, false, false>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: PartSupportVendorStatusOption,
        state: AutocompleteRenderOptionState,
        defaultRender: (props: React.HTMLAttributes<HTMLLIElement>, option: PartSupportVendorStatusOption) => React.ReactNode
    ) => React.ReactNode;

    [key: string]: any;
}

/**
 * PartSupportVendorStatusAutocomplete Component
 *
 * A searchable dropdown component for selecting PartSupportVendorStatuss with the following features:
 * - Asynchronous search with debouncing
 * - Custom option rendering with name and city display
 * - Loading state management
 * - Form integration support
 * - Customizable styling and behavior
 *
 * @component
 */
const PartSupportVendorStatusAutocomplete = <M extends boolean = false, >({
                                                                              textFieldProps = {},
                                                                              form = false,
                                                                              name = undefined,
                                                                              required = false,
                                                                              setLoadingOptions: setLoadingOptionsProps,
                                                                              renderOption: customRenderOption,
                                                                              ...props
                                                                          }: PartSupportVendorStatusAutocompleteProps<M>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches PartSupportVendorStatus options based on search keywords
     * Handles loading state and component unmounting
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string
     * @returns Array of matching PartSupportVendorStatus options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken) => {
        setLoadingOptions(true);
        try {
            // @ts-expect-error Api method exists but is not yet typed
            const response = (await Api.getPartSupportVendorStatuses(cancelToken));
            if (!isMounted())
                return [];
            return (response?.data ?? []) as PartSupportVendorStatusOption[];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted]);

    /**
     * Determines if two PartSupportVendorStatus options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: PartSupportVendorStatusOption, value: PartSupportVendorStatusOption | null) =>
        option.id === value?.id || (option.title === value?.title && option.title != null), []);

    /**
     * Extracts the display label from a PartSupportVendorStatus option
     */
    const getOptionLabel = useCallback((option: PartSupportVendorStatusOption) => `${option?.title ?? ""}`, []);

    /**
     * Gets the unique key for a PartSupportVendorStatus option
     */
    const getOptionKey = useCallback((option: PartSupportVendorStatusOption) => option?.id ?? ((option.title != null) ? `title_${option.title}` : null) ?? "", []);

    // GetOptions will populate an initial empty array
    const defaultOptions = useMemo(() => [], []);

    return (
        <AutocompleteBase<PartSupportVendorStatusOption, M>
            canSearchEmptyKeywords
            keywordsBased={false}
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={defaultOptions}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default PartSupportVendorStatusAutocomplete;